import { useQuery } from "@tanstack/react-query";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { fetchSession } from "./api.js";
import { Landing } from "./screens/Landing.js";
import { Privacy } from "./screens/Privacy.js";
import { Terms } from "./screens/Terms.js";
import { SignIn } from "./screens/SignIn.js";
import { Connections } from "./screens/Connections.js";
import { Availability } from "./screens/Availability.js";
import { OwnerShell } from "./screens/OwnerShell.js";
import { Requests } from "./screens/Requests.js";
import { Calendar } from "./screens/Calendar.js";
import { Invitations } from "./screens/Invitations.js";
import { VisitorConfirm } from "./screens/VisitorConfirm.js";
import { Booking } from "./screens/Booking.js";
import { VisitorRequests } from "./screens/VisitorRequests.js";
import "./app.css";

function Routed() {
  const session = useQuery({ queryKey: ["session"], queryFn: fetchSession });

  if (session.isPending) {
    return (
      <main className="centered">
        <p className="lede">Checking your session…</p>
      </main>
    );
  }

  const owner = session.data?.signedIn ? session.data.owner : undefined;

  return (
    <Routes>
      <Route
        path="/sign-in"
        element={owner ? <Navigate to="/connections" replace /> : <SignIn />}
      />
      <Route
        path="/connections"
        element={
          owner ? (
            <OwnerShell owner={owner}>
              <Connections owner={owner} />
            </OwnerShell>
          ) : (
            <Navigate to="/sign-in" replace />
          )
        }
      />
      <Route
        path="/availability"
        element={
          owner ? (
            <OwnerShell owner={owner}>
              <Availability />
            </OwnerShell>
          ) : (
            <Navigate to="/sign-in" replace />
          )
        }
      />
      <Route
        path="/requests"
        element={
          owner ? (
            <OwnerShell owner={owner}>
              <Requests timezone={owner.timezone} />
            </OwnerShell>
          ) : (
            <Navigate to="/sign-in" replace />
          )
        }
      />
      <Route
        path="/calendar"
        element={
          owner ? (
            <OwnerShell owner={owner}>
              <Calendar timezone={owner.timezone} />
            </OwnerShell>
          ) : (
            <Navigate to="/sign-in" replace />
          )
        }
      />

      <Route
        path="/invitations"
        element={
          owner ? (
            <OwnerShell owner={owner}>
              <Invitations />
            </OwnerShell>
          ) : (
            <Navigate to="/sign-in" replace />
          )
        }
      />

      {/* Visitor routes are never behind an owner session. */}
      <Route path="/i/:token" element={<VisitorConfirm />} />
      <Route path="/book" element={<Booking />} />
      <Route path="/my-requests" element={<VisitorRequests />} />

      {/* Legal pages answer for everyone, signed in or not - a reviewer with no
          account has to be able to read them, and Google's verification checks
          that the privacy policy is reachable without one. */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />

      {/* The only page a stranger can arrive at. A signed-in owner is past it. */}
      <Route
        path="/"
        element={owner ? <Navigate to="/connections" replace /> : <Landing />}
      />

      <Route path="*" element={<Navigate to={owner ? "/connections" : "/"} replace />} />
    </Routes>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Routed />
    </BrowserRouter>
  );
}
